<script setup lang="ts">
import { defineProps, defineEmits, ref } from "vue";
import BaseModal from "@/views/Auth/components/BaseModal.vue";
import { useI18n } from "vue-i18n";
import { AuthModalMode } from "@/types/auth";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import LoginModalContent from "@/views/Auth/components/ModalContent/LoginModalContent.vue";
import SignupModalContent from "@/views/Auth/components/ModalContent/SignupModalContent.vue";
import ForgotPasswordModalContent from "@/views/Auth/components/ModalContent/ForgotPasswordModalContent.vue";
import { useUserStore } from "@/stores/userStore";
import buddhaImage from "@/assets/buddha.webp";
import { fetchForgotPassword } from "@/api/user";
import { useToastNotificationStore } from "@/stores/toastNotificationStore";

defineProps({
  show: Boolean,
});

const emits = defineEmits(["close"]);

const { t } = useI18n();
const userStore = useUserStore();
const toastStore = useToastNotificationStore();

const modalMode = ref<AuthModalMode>("login");
const waitApiCall = ref(false);

const modalTitle = {
  login: "auth.login-title",
  signup: "auth.signup-title",
  forgot: "auth.forgot-password-title",
};

function changeModalMode(newMode: AuthModalMode) {
  modalMode.value = newMode;
}

async function handleLogin(email: string, password: string) {
  try {
    waitApiCall.value = true;
    await userStore.login(email, password);
    emits("close");
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}

async function handleSignUp(
  email: string,
  password: string,
  passwordConfirm: string
) {
  try {
    waitApiCall.value = true;
    await userStore.signUp({
      email,
      password,
      password_confirmation: passwordConfirm,
    });
    emits("close");
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}

async function handleForgotPassword(email: string) {
  try {
    waitApiCall.value = true;
    await fetchForgotPassword(email);
    await toastStore.createNotificationFromMessage(
      t("auth.reset-password-success")
    );
    modalMode.value = "login";
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <BaseModal
    :show="show"
    :left-image-asset="buddhaImage"
    @close="emits('close')"
  >
    <!-- TITLE -->
    <div class="flex gap-2">
      <ArrowIcon
        class="text-black -rotate-[135deg] size-8 cursor-pointer"
        v-if="modalMode !== 'login'"
        @click="changeModalMode('login')"
      />
      <p class="text-2xl font-semibold text-black mb-10 select-none">
        {{ t(modalTitle[modalMode]) }}
      </p>
    </div>
    <LoginModalContent
      v-if="modalMode === 'login'"
      :waitApiCall="waitApiCall"
      @change-modal-mode="changeModalMode"
      @login="handleLogin"
    />
    <SignupModalContent
      v-else-if="modalMode === 'signup'"
      :waitApiCall="waitApiCall"
      @signup="handleSignUp"
    />
    <ForgotPasswordModalContent
      v-else-if="modalMode === 'forgot'"
      :waitApiCall="waitApiCall"
      @forgot-password="handleForgotPassword"
    />
  </BaseModal>
</template>
